/**
 * Early JWT Extraction
 * * This module extracts the JWT from URL parameters BEFORE the store is initialized.
 * This solves the circular dependency where:
 * 1. Feature flags need a JWT to make API calls
 * 2. JWT is normally extracted by router navigation guards
 * 3. Router runs AFTER store initialization
 * 4. Store initialization tries to fetch feature flags (without JWT)
 *
 * By extracting the JWT early, we can ensure it's available in localStorage
 * when the store rehydrates and tries to fetch feature flags.
 */

import { logger } from '@/shared/logging';
import { localStoreKey } from '@/shared/shared.utils';
import jwtDecode from 'jwt-decode';

const log = logger('early-jwt');

type JwtClaims = {
    email: string;
    firstName: string;
    id: number;
    lastName: string;
    displayName: string;
    exp: number;
};

/**
 * Extract JWT from URL parameters before store initialization
 * This allows the JWT to be available when feature flags are fetched
 */
export function extractJwtFromUrl(): string | null {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const ticket = urlParams.get('ticket') || urlParams.get('userToken');

        if (ticket) {
            log.info('Found JWT in URL parameters', { hasTicket: !!ticket });

            // Validate the JWT before using it
            try {
                const decoded = jwtDecode<JwtClaims>(ticket);
                const isExpired = decoded.exp ? decoded.exp * 1000 < Date.now() : true;

                if (isExpired) {
                    log.warn('JWT in URL is expired, not using it');

                    return null;
                }

                // Store the JWT in localStorage so it's available when store rehydrates
                const persistedKey = localStoreKey('vuex');
                const existingStore = localStorage.getItem(persistedKey);

                if (existingStore) {
                    try {
                        const store = JSON.parse(existingStore);

                        if (!store.auth) store.auth = { session: {}, user: null };
                        if (!store.auth.session) store.auth.session = {};

                        store.auth.session.jwt = ticket;
                        store.auth.hasEverLoggedIn = true;
                        store.auth.user = {
                            id: decoded.id,
                            username: decoded.email,
                            givenName: decoded.firstName,
                            familyName: decoded.lastName,
                            fullName: decoded.displayName,
                            exp: decoded.exp,
                        };

                        localStorage.setItem(persistedKey, JSON.stringify(store));
                        log.info('JWT extracted and stored in localStorage before store initialization');
                    } catch (e) {
                        log.error('Failed to parse existing store', e);
                    }
                } else {
                    const minimalStore = {
                        auth: {
                            session: { jwt: ticket },
                            hasEverLoggedIn: true,
                            user: {
                                id: decoded.id,
                                username: decoded.email,
                                givenName: decoded.firstName,
                                familyName: decoded.lastName,
                                fullName: decoded.displayName,
                                exp: decoded.exp,
                            },
                        },
                    };

                    localStorage.setItem(persistedKey, JSON.stringify(minimalStore));
                    log.info('Created new store in localStorage with JWT');
                }

                return ticket;
            } catch (decodeError) {
                log.error('Failed to decode JWT from URL', decodeError);

                return null;
            }
        } else {
            log.debug('No JWT found in URL parameters');

            return null;
        }
    } catch (error) {
        log.error('Error extracting JWT from URL', error);

        return null;
    }
}
